// React Query hooks wrapping the API. One source of truth for query keys so
// mutations can invalidate precisely.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ProductConfigPatch } from './api';
import type { ExternalEvent, Settings } from '@shared/types';

export const keys = {
  session: ['session'] as const,
  dashboard: ['dashboard'] as const,
  products: ['products'] as const,
  combos: ['combos'] as const,
  recommendations: ['recommendations'] as const,
  orders: ['orders'] as const,
  suppliers: ['suppliers'] as const,
  alerts: ['alerts'] as const,
  events: ['events'] as const,
  unmet: ['unmet-demand'] as const,
  forecast: ['forecast'] as const,
  settings: ['settings'] as const,
  importTemplates: ['import-templates'] as const,
  ingest: ['ingest'] as const,
};

export const useDashboard = () => useQuery({ queryKey: keys.dashboard, queryFn: api.dashboard });
export const useProducts = () => useQuery({ queryKey: keys.products, queryFn: api.products });
export const useCombos = () => useQuery({ queryKey: keys.combos, queryFn: api.combos });
/** La ficha de un SKU. `enabled` la apaga mientras no haya ninguno elegido, así abrir la
 *  pantalla no dispara una consulta que nadie pidió. */
export const useFicha = (sku: string | null) =>
  useQuery({
    queryKey: ['ficha', sku] as const,
    queryFn: () => api.ficha(sku!),
    enabled: sku != null,
  });

export const useRecommendations = () => useQuery({ queryKey: keys.recommendations, queryFn: api.recommendations });
export const useOrders = () => useQuery({ queryKey: keys.orders, queryFn: api.orders });
export const useAlerts = () => useQuery({ queryKey: keys.alerts, queryFn: api.alerts });
export const useEvents = () => useQuery({ queryKey: keys.events, queryFn: api.events });
export const useUnmetDemand = () => useQuery({ queryKey: keys.unmet, queryFn: api.unmetDemand });
export const useForecast = () => useQuery({ queryKey: keys.forecast, queryFn: api.forecast });
export const useSettings = () => useQuery({ queryKey: keys.settings, queryFn: api.settings });
export const useImportTemplates = () => useQuery({ queryKey: keys.importTemplates, queryFn: api.importTemplates });
export const useIngestStatus = () => useQuery({ queryKey: keys.ingest, queryFn: api.ingestStatus });

/** Actualiza los datos desde el B2B. Al terminar refresca TODA la app: la ingesta reescribe
 *  productos, venta, faltantes y stock, así que no queda pantalla que no haya cambiado. */
export function useRunIngest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runIngest(),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useApproveRecommendation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.approveRecommendation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.recommendations });
      qc.invalidateQueries({ queryKey: keys.dashboard });
      // Aprobar CREA una orden de compra: sin esto, /ordenes seguía mostrando la lista vieja.
      qc.invalidateQueries({ queryKey: keys.orders });
    },
  });
}

/** Aprobar varias de una. El servidor topea el lote, así que se manda en tandas: si una
 *  falla, lo que ya entró queda aprobado y se informa hasta dónde llegó. */
export function useApproveRecommendationBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const TANDA = 100;
      let aprobadas = 0;
      let salteadas = 0;
      for (let i = 0; i < ids.length; i += TANDA) {
        const r = await api.approveRecommendationBatch(ids.slice(i, i + TANDA));
        aprobadas += r.aprobadas;
        salteadas += r.salteadas;
      }
      return { aprobadas, salteadas };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.recommendations });
      qc.invalidateQueries({ queryKey: keys.dashboard });
      qc.invalidateQueries({ queryKey: keys.orders });
    },
  });
}

/** Sube la planilla completada. Va en tandas porque el server topea el lote: si una falla,
 *  lo que ya entró queda aplicado y se informa hasta dónde llegó. */
export function useSubirPlanilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (filas: Record<string, unknown>[]) => {
      const TANDA = 200;
      let actualizados = 0;
      const noEncontrados: string[] = [];
      const errores: { sku: string; motivo: string }[] = [];
      for (let i = 0; i < filas.length; i += TANDA) {
        const r = await api.subirPlanilla(filas.slice(i, i + TANDA));
        actualizados += r.actualizados;
        noEncontrados.push(...r.noEncontrados);
        errores.push(...r.errores);
      }
      return { actualizados, noEncontrados, errores };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.products });
      qc.invalidateQueries({ queryKey: keys.suppliers });
      qc.invalidateQueries({ queryKey: keys.forecast });
      qc.invalidateQueries({ queryKey: keys.settings });
    },
  });
}

export const useSuppliers = () => useQuery({ queryKey: keys.suppliers, queryFn: api.suppliers });

/** Todo lo que toca un proveedor cambia el lead time de sus productos, y de ahí cuelgan las
 *  fechas y las cantidades de compra: se invalida el forecast y el semáforo, no sólo la lista. */
function useMutacionProveedor<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.suppliers });
      qc.invalidateQueries({ queryKey: keys.products });
      qc.invalidateQueries({ queryKey: keys.forecast });
      qc.invalidateQueries({ queryKey: keys.settings });
    },
  });
}

export const useCreateSupplier = () =>
  useMutacionProveedor((v: Record<string, unknown>) => api.createSupplier(v));
export const useUpdateSupplier = () =>
  useMutacionProveedor((v: { id: number; patch: Record<string, unknown> }) => api.updateSupplier(v.id, v.patch));
export const useDeleteSupplier = () => useMutacionProveedor((id: number) => api.deleteSupplier(id));
export const useAssignSupplier = () =>
  useMutacionProveedor((v: { id: number; skus: string[] }) => api.assignSupplier(v.id, v.skus));

export function useAdjustRecommendation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; qty: number; note?: string }) =>
      api.adjustRecommendation(vars.id, vars.qty, vars.note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.recommendations });
      qc.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<ExternalEvent>) => api.createEvent(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.events }),
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Settings>) => api.updateSettings(payload),
    onSuccess: (data) => qc.setQueryData(keys.settings, data),
  });
}

/** Nivel de servicio / cobertura de UNA categoría (ticket 11). */
export function useUpdateCategorySetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { category: string; serviceLevel: number | null; coverageTargetDays: number | null }) =>
      api.updateCategorySetting(payload),
    onSuccess: (data) => qc.setQueryData(keys.settings, data),
  });
}

/** Edita la configuración de un producto. Invalida también dashboard/inventario:
 *  el costo unitario alimenta el valor del inventario y las cuentas de compra. */
export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sku, patch }: { sku: string; patch: ProductConfigPatch }) =>
      api.updateProduct(sku, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.products });
      qc.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

/** Aplica los mismos valores a varios SKU. */
export function useUpdateProductsBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skus, patch }: { skus: string[]; patch: ProductConfigPatch }) =>
      api.updateProductsBulk(skus, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.products });
      qc.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

// ── Sesión ──────────────────────────────────────────────────────────────────────

/** Sesión actual. `retry: false` para que un 401 no reintente tres veces antes del login. */
export const useSession = () =>
  useQuery({ queryKey: keys.session, queryFn: api.me, retry: false, staleTime: 60_000 });

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => api.login(email, password),
    onSuccess: (user) => qc.setQueryData(keys.session, { authed: true, user }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.logout(),
    // Se limpia TODO: las queries cacheadas tienen datos del cliente y no deben sobrevivir
    // a un logout (ni quedar visibles si entra otra persona).
    onSuccess: () => qc.clear(),
  });
}

/** Recalcula el forecast sin volver a ingerir. Lo usa el semáforo después de editar
 *  tiempos de producción o costos: son los que mueven el punto de reorden y las fechas. */
export function useRecalcularForecast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.recalcularForecast(),
    onSuccess: () => qc.invalidateQueries(),
  });
}
